import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  Rating,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api, { getMediaUrl } from '../services/api';
import BackgroundBanner from '../components/BackgroundBanner';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import TimerIcon from '@mui/icons-material/Timer';
import HandshakeIcon from '@mui/icons-material/Handshake';

const Home = () => {
  const theme = useTheme();
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);

  // Background banner images - images from /images/background folder
  const bannerImages = [
    '/images/background/banner-1.jpg',
    '/images/background/banner-2.jpg',
    '/images/background/banner-3.jpg',
    '/images/background/banner-4.jpg',
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [areasRes, testimonialsRes, blogsRes] = await Promise.all([
          api.get('/practice-areas'),
          api.get('/testimonials'),
          api.get('/blogs?limit=3'),
        ]);
        const areas = Array.isArray(areasRes.data) ? areasRes.data : [];
        const testimonials = Array.isArray(testimonialsRes.data) ? testimonialsRes.data : [];
        const blogPayload = blogsRes.data;
        const blogsList = Array.isArray(blogPayload?.blogs)
          ? blogPayload.blogs
          : Array.isArray(blogPayload)
            ? blogPayload
            : [];
        setPracticeAreas(areas.slice(0, 6));
        setTestimonials(testimonials.slice(0, 3));
        setBlogs(blogsList);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          color: 'white',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        <BackgroundBanner images={bannerImages} interval={5000} />
        <Container sx={{ position: 'relative', zIndex: 3 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  mb: 2,
                  opacity: 0.9,
                  fontSize: '0.9rem',
                  letterSpacing: '0.15em',
                }}
              >
                Welcome to Advocate Priyanka Swami
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Advocates, Solicitors & Legal Consultants
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.95, fontWeight: 400 }}>
                Member of Hon&apos;ble Supreme Court, High Court & District Court Bar Associations
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, fontSize: '1.1rem', lineHeight: 1.8 }}>
                We serve you on priority with consultation from our experienced attorneys ensuring
                professional confidentiality. Primarily practising environmental law before the
                National Green Tribunal and higher courts, while also handling select civil, criminal
                and commercial matters with strategic, result‑oriented counsel.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  component={Link}
                  to="/appointment"
                >
                  Book Consultation
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  component={Link}
                  to="/about"
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                {/* Portrait */}
                <Card
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 18px 45px rgba(15, 23, 42, 0.5)',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="360"
                    image="/images/priyanka-portrait.jpg"
                    alt="Advocate Priyanka Swami"
                    sx={{ objectFit: 'cover' }}
                  />
                </Card>

                {/* Availability card */}
                <Paper
                  elevation={6}
                  sx={{
                    p: 3,
                    width: '100%',
                    maxWidth: 360,
                    borderRadius: 3,
                    backgroundColor: 'rgba(15,23,42,0.9)',
                    border: '1px solid rgba(148,163,184,0.4)',
                    color: 'common.white',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.light' }}>
                    Featured Availability
                  </Typography>
                  <Typography variant="h6" color="inherit" sx={{ mb: 1 }}>
                    Schedule a Private Consultation
                  </Typography>
                  <Typography variant="body2" color="inherit" sx={{ mb: 3, opacity: 0.85 }}>
                    Select a time that works for you and receive tailored guidance on your matter.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    component={Link}
                    to="/appointment"
                  >
                    View Available Slots
                  </Button>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Statistics Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  2010
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Leading Since
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  500+
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Cases Handled
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  200+
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Happy Clients
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  92%
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Success Ratio
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Our Best Service Benefits
          </Typography>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <WorkspacePremiumIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Comprehensive Single-Window Solutions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One-stop legal service hub combining advocates, solicitors, and legal consultants
                under one roof.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <PeopleIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Experienced Legal Team
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Years of experience in various legal domains with a proven track record of success
                and expertise.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Client-Focused Approach
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dedicated to understanding your needs and providing personalized, tailored solutions.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <TimerIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Efficient & Timely Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Well-timed services without delay, making timely resolution our main essence.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <VerifiedUserIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Trusted Legal Guidance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Committed to achieving the best possible outcomes with integrity and professionalism.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <AccountBalanceIcon sx={{ fontSize: 56, color: theme.palette.secondary.main, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Wide Range of Services
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Strong presence in environmental, civil, criminal, and commercial legal matters.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* How We Work Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              The Way We Work
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Our systematic approach ensures professional confidentiality and maximizes success for
              every client
            </Typography>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  1
                </Box>
                <Typography variant="h6" gutterBottom>
                  Consultation with Confidentiality
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We discuss your case and consult you with the latest laws ensuring your privacy
                  and confidentiality as our pivotal aspect.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  2
                </Box>
                <Typography variant="h6" gutterBottom>
                  Case Evaluation and Strategy Making
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our team analyzes your case from every corner and customizes a tailored strategy
                  to maximize success.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  3
                </Box>
                <Typography variant="h6" gutterBottom>
                  Time Bound Strong Representation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We vehemently advocate for your rights and provide well-timed services without any
                  delay, making timed services our main essence.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                  }}
                >
                  4
                </Box>
                <Typography variant="h6" gutterBottom>
                  Resolution & Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We achieve the best outcome and provide ongoing assistance throughout the legal
                  journey and thereafter.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Practice Areas */}
      <Container sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Trusted Legal Guidance Across Diverse Practice Areas
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Our Expertise
          </Typography>
        </Box>
          <Grid container spacing={4}>
            {practiceAreas.length > 0 ? (
              practiceAreas.map((area) => (
                <Grid item xs={12} sm={6} md={4} key={area._id}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {area.image && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={getMediaUrl(area.image)}
                        alt={area.title}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {area.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {area.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" align="center" color="text.secondary">
                  Practice areas will be displayed here. Please check back soon.
                </Typography>
              </Grid>
            )}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" component={Link} to="/practice-areas" size="large">
              View All Practice Areas
            </Button>
          </Box>
        </Container>

      {/* Testimonials */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              Hear From Our Clients
            </Typography>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Client Testimonials
            </Typography>
          </Box>
        <Grid container spacing={4}>
          {testimonials.length > 0 ? (
            testimonials.map((testimonial) => (
              <Grid item xs={12} md={4} key={testimonial._id}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    boxShadow: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Rating value={testimonial.rating} readOnly sx={{ mb: 2, color: theme.palette.secondary.main }} />
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      fontStyle: 'italic',
                      fontSize: '1rem',
                      lineHeight: 1.8,
                      color: 'text.primary',
                      flexGrow: 1,
                    }}
                  >
                    &quot;{testimonial.content}&quot;
                  </Typography>
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      {testimonial.clientName}
                    </Typography>
                    {testimonial.caseType && (
                      <Typography variant="caption" color="text.secondary">
                        {testimonial.caseType}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" align="center" color="text.secondary">
                No testimonials available yet. Please check back soon.
              </Typography>
            </Grid>
          )}
        </Grid>
        </Container>
      </Box>

      {/* Blog Preview Section */}
      {blogs.length > 0 && (
        <Container sx={{ py: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              News, Tips and Legal Updates
            </Typography>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              News, Tips and Legal Updates
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {blogs.slice(0, 3).map((blog) => (
              <Grid item xs={12} md={4} key={blog._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  {blog.featuredImage && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={getMediaUrl(blog.featuredImage)}
                      alt={blog.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {blog.category && (
                      <Typography variant="caption" color="secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        {blog.category}
                      </Typography>
                    )}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {blog.title}
                    </Typography>
                    {blog.excerpt && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {blog.excerpt.length > 120 ? `${blog.excerpt.substring(0, 120)}...` : blog.excerpt}
                      </Typography>
                    )}
                    <Button
                      component={Link}
                      to={`/blog/${blog.slug}`}
                      variant="text"
                      color="primary"
                      sx={{ alignSelf: 'flex-start', mt: 'auto' }}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" component={Link} to="/blog" size="large">
              Explore All Blogs
            </Button>
          </Box>
        </Container>
      )}

      {/* Appointment Booking Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  Book An Appointment
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
                  Schedule a consultation to discuss your legal needs. Fill out the form and we&apos;ll
                  get back to you promptly to confirm your appointment.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon sx={{ color: 'secondary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Flexible scheduling available
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'secondary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Expert consultation and guidance
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ color: 'secondary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Confidential and professional service
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  boxShadow: 4,
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Quick Appointment Request
                </Typography>
                <Box
                  component="form"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    component={Link}
                    to="/appointment"
                    size="large"
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    Book Appointment Now
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Or call us directly at +91 88006 56660
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container>
          <ScheduleIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Need Legal Assistance?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Schedule a consultation today and let us help you navigate your legal challenges.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            to="/appointment"
          >
            Book Appointment
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
